import { useState, useEffect, useRef } from 'react';
import { Stack, MenuItem, TextField, Popover, InputAdornment, IconButton } from '@mui/material';
import { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import {
  Button,
  Typography,
  Autocomplete,
  AutocompleteOption,
} from '@occc-app/design-system';
import { DateRangePicker } from '@occc/ui-design-system';
import {
  External_Loyalty_Manual_Offer_Manufacturer,
} from '@occc/graphql';
import { RetailerNetworkSingleSelectField } from '@occc/feat-retailer-connect';

import unitedStates from '@occc-app/lib/constants/unitedStates';
import { formatToUTCDateString } from '@occc-app/lib/date';

import { OfferTableType, OffersFilterValues } from '../../../tobacco-api-offers.types';
import {
  DEFAULT_FILTER_VALUES,
  EXTERNAL_LOYALTY_OFFER_STATUSES,
  STATUS_LABEL,
} from '../../../tobacco-api-offers.constants';

import { BrandAutocomplete } from './brand-autocomplete/brand-autocomplete.component';

type OffersFilterFormProps = {
  tableType: OfferTableType;
  isOpen: boolean;
  initialValues: OffersFilterValues;
  onApply: (values: OffersFilterValues) => void;
  onReset: () => void;
  onClose: () => void;
};

export const OffersFilterForm = ({
  tableType,
  isOpen,
  initialValues,
  onApply,
  onReset,
  onClose,
}: OffersFilterFormProps) => {
  const [values, setValues] = useState<OffersFilterValues>(initialValues);
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);
  const dateFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const stateOptions: AutocompleteOption[] = unitedStates.map(({ id, text }) => ({
    value: id,
    label: text,
  }));

  const selectedStates = stateOptions.filter((option) =>
    values.states.includes(option.value),
  );

  const handleDatePickerOpen = () => {
    setDatePickerAnchor(dateFieldRef.current);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDatesChange = (startDate: Date, endDate: Date) => {
    setValues((prev) => ({
      ...prev,
      runDatesStart: formatToUTCDateString(startDate).split('T')[0],
      runDatesEnd: formatToUTCDateString(endDate).split('T')[0],
    }));
    handleDatePickerClose();
  };

  const formatDateRange = () => {
    if (!values.runDatesStart && !values.runDatesEnd) return '';
    if (values.runDatesStart && values.runDatesEnd) {
      return `${values.runDatesStart} - ${values.runDatesEnd}`;
    }
    if (values.runDatesStart) return `From ${values.runDatesStart}`;
    return `Until ${values.runDatesEnd}`;
  };

  const handleApply = () => {
    onApply(values);
    onClose();
  };

  const handleReset = () => {
    setValues(DEFAULT_FILTER_VALUES);
    onReset();
    onClose();
  };

  const handleStatesChange = (_: React.ChangeEvent<{}>, newValue: unknown) => {
    const options = Array.isArray(newValue) ? newValue : [];
    const stateValues = options
      .filter((o): o is AutocompleteOption => !Array.isArray(o) && o !== null)
      .map((o) => o.value);
    setValues((prev) => ({ ...prev, states: stateValues }));
  };

  return (
    <Stack p={3} spacing={3}>
      <Typography variant='h6'>Filter Offers</Typography>

      <RetailerNetworkSingleSelectField
        isOpen={isOpen}
        value={
          values.retailerNetworkUuid
            ? { uuid: values.retailerNetworkUuid, name: values.retailerNetworkName }
            : null
        }
        onChange={(network) =>
          setValues((prev) => ({
            ...prev,
            retailerNetworkUuid: network?.uuid ?? '',
            retailerNetworkName: network?.name ?? '',
          }))
        }
      />

      <BrandAutocomplete
        tableType={tableType}
        isOpen={isOpen}
        value={values.brand}
        onChange={(brand) => setValues((prev) => ({ ...prev, brand }))}
      />

      <TextField
        select
        id={`${tableType}-filter-manufacturer`}
        name={`${tableType}-manufacturer`}
        label='Manufacturer'
        value={values.manufacturer}
        onChange={(e) => setValues((prev) => ({ ...prev, manufacturer: e.target.value }))}
        size='small'
        fullWidth
      >
        <MenuItem value=''>All</MenuItem>
        {Object.values(External_Loyalty_Manual_Offer_Manufacturer).map((m) => (
          <MenuItem key={m} value={m}>
            {m}
          </MenuItem>
        ))}
      </TextField>

      <Autocomplete
        multiple
        options={stateOptions}
        value={selectedStates}
        onChange={handleStatesChange}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            id={`${tableType}-filter-states`}
            name={`${tableType}-states`}
            label='States'
            size='small'
          />
        )}
        size='small'
      />

      <TextField
        ref={dateFieldRef}
        id={`${tableType}-filter-run-dates`}
        name={`${tableType}-runDates`}
        label='Run Dates'
        value={formatDateRange()}
        onClick={handleDatePickerOpen}
        size='small'
        fullWidth
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton size='small' edge='end'>
                <CalendarTodayIcon fontSize='small' />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ cursor: 'pointer' }}
      />

      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={handleDatePickerClose}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        <DateRangePicker
          show={true}
          startDate={values.runDatesStart ? new Date(values.runDatesStart) : new Date()}
          endDate={values.runDatesEnd ? new Date(values.runDatesEnd) : new Date()}
          disableFuture={false}
          onDatesChange={handleDatesChange}
          onClose={handleDatePickerClose}
        />
      </Popover>

      <TextField
        select
        id={`${tableType}-filter-is-errored`}
        name={`${tableType}-isErrored`}
        label='Errors'
        value={values.isErrored}
        onChange={(e) =>
          setValues((prev) => ({
            ...prev,
            isErrored: e.target.value as OffersFilterValues['isErrored'],
          }))
        }
        size='small'
        fullWidth
      >
        <MenuItem value='all'>All</MenuItem>
        <MenuItem value='yes'>Yes</MenuItem>
        <MenuItem value='no'>No</MenuItem>
      </TextField>

      <TextField
        select
        id={`${tableType}-filter-status`}
        name={`${tableType}-status`}
        label='Status'
        value={values.status}
        onChange={(e) => setValues((prev) => ({ ...prev, status: e.target.value }))}
        size='small'
        fullWidth
      >
        <MenuItem value=''>All</MenuItem>
        {EXTERNAL_LOYALTY_OFFER_STATUSES.map((s) => (
          <MenuItem key={s} value={s}>
            {STATUS_LABEL[s]}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        id={`${tableType}-filter-name`}
        name={`${tableType}-name`}
        label='Offer Name'
        value={values.name}
        onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
        size='small'
        fullWidth
      />

      <TextField
        id={`${tableType}-filter-external-campaign-id`}
        name={`${tableType}-externalCampaignId`}
        label='Campaign ID'
        value={values.externalCampaignId}
        onChange={(e) => setValues((prev) => ({ ...prev, externalCampaignId: e.target.value }))}
        size='small'
        fullWidth
      />

      <Stack direction='row' justifyContent='flex-end' spacing={2} pt={1}>
        <Button onClick={handleReset}>
          Reset
        </Button>
        <Button variant='contained' color='primary' onClick={handleApply}>
          Apply
        </Button>
      </Stack>
    </Stack>
  );
};
